import type { SiteDto } from '@app/contracts';

interface Props {
  sites: SiteDto[];
  selected: Set<string>;
  preferred: string | null;
  pending?: boolean;
  onToggle: (siteId: string) => void;
  onPreferred: (siteId: string) => void;
}

export function SitePicker({ sites, selected, preferred, pending, onToggle, onPreferred }: Props) {
  if (!sites.length) return <p className="text-sm text-slate-400">Aucun site disponible.</p>;

  return (
    <div className="space-y-2">
      {sites.map((site) => {
        const checked = selected.has(site.id);
        const isPref = preferred === site.id;
        return (
          <div
            key={site.id}
            className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 transition-colors
              ${checked ? 'border-teal-200 bg-teal-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'}
              ${pending ? 'opacity-60' : ''}`}
          >
            {/* Checkbox */}
            <button
              type="button"
              disabled={pending}
              onClick={() => onToggle(site.id)}
              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors
                ${
                  checked
                    ? 'border-teal-500 bg-teal-500 text-white'
                    : 'border-slate-300 bg-white hover:border-teal-400'
                }`}
              aria-checked={checked}
            >
              {checked && (
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                  aria-hidden
                >
                  <title>Sélectionné</title>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>

            {/* Site name + city */}
            <span
              className={`flex-1 text-sm font-medium ${checked ? 'text-slate-900' : 'text-slate-500'}`}
            >
              {site.name}
              {site.city && (
                <span className="ml-1.5 text-[0.72rem] font-normal text-slate-400">
                  — {site.city}
                </span>
              )}
            </span>

            {/* Star — site préféré (visible uniquement si coché) */}
            {checked && (
              <button
                type="button"
                disabled={pending}
                onClick={() => onPreferred(site.id)}
                title={isPref ? 'Site préféré' : 'Définir comme site préféré'}
                className={`flex-shrink-0 transition-colors ${
                  isPref
                    ? 'text-amber-400 hover:text-amber-500'
                    : 'text-slate-300 hover:text-amber-300'
                }`}
              >
                <svg
                  className="h-5 w-5"
                  fill={isPref ? 'currentColor' : 'none'}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  aria-hidden
                >
                  <title>{isPref ? 'Étoile site préféré' : 'Définir site préféré'}</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
